import { preprocessEmptyStringAsUndefined } from '@dealstack/shared/lib/common/common.ts';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n.ts';
import { SecurityEventType } from '@dealstack/shared/lib/common/securities/securities.ts';
import {
	CurrencyType,
	SecurityEquityBasis,
	SecurityIssuedIn,
	SecurityType,
} from '@dealstack/shared/lib/generated';
import { z } from 'zod';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types';

export const CreateSecurityShape = z.object({
	name: z.string().trim().min(1),
	personId: z.string(),
	type: z.nativeEnum(SecurityType, { invalid_type_error: 'Required' }),
	currency: z.nativeEnum(CurrencyType, { invalid_type_error: 'Required' }),
});
export type CreateSecurityFormDto = z.infer<typeof CreateSecurityShape>;

export const CreateSecurityEventShape = z
	.object({
		date: z
			.string({
				required_error: 'The date is required',
				invalid_type_error: 'The mandatory field is empty',
			})
			.datetime({ message: 'The date must be a valid ISO 8601 string' }),
		preEventSecurityId: z.string().nonempty({ message: 'securityId is required' }),
		postEventSecurityId: z.string(),
		type: z.nativeEnum(SecurityEventType, { invalid_type_error: 'Required' }),
		securityDetailsNominalValue: z.number().nonnegative().optional(),
		preEventUnits: z.number().nonnegative().optional(),
		postEventUnits: z.number().nonnegative().optional(),
		postEventNominalValue: z.number().nonnegative().optional(),
		preEventNominalValue: z.number().nonnegative().optional(),
	})
	.refine(
		(data) => {
			if (data.type === SecurityEventType.CONVERSION) {
				return !!data.postEventSecurityId && data.postEventSecurityId.trim() !== '';
			}
			return true;
		},
		{
			message: 'Select a security for conversion',
			path: ['postEventSecurityId'],
		}
	)
	.refine(
		(data) => {
			if (data.type === SecurityEventType.SPLIT) {
				return !!data.postEventUnits && data.postEventUnits > 0;
			}
			return true;
		},
		{
			message: 'Enter a valid number of units',
			path: ['postEventUnits'],
		}
	)
	.refine(
		(data) => {
			if (data.type === SecurityEventType.REVERSE_SPLIT) {
				return !!data.preEventUnits && data.preEventUnits > 0;
			}
			return true;
		},
		{
			message: 'Enter a valid number of units',
			path: ['preEventUnits'],
		}
	)
	.refine(
		(data) => {
			if (data.type === SecurityEventType.ADJUST) {
				return !!data.securityDetailsNominalValue && data.securityDetailsNominalValue >= 0;
			}
			return true;
		},
		{
			message: 'Enter a valid nominal value',
			path: ['securityDetailsNominalValue'],
		}
	);

export type CreateSecurityEventDto = z.infer<typeof CreateSecurityEventShape>;

export const UpdateSecurityEventShape = CreateSecurityEventShape.and(z.object({ id: z.string() }));
export type UpdateSecurityEventDto = z.infer<typeof UpdateSecurityEventShape>;

export const UpdateSecurityShape = CreateSecurityShape.and(z.object({ id: z.string() }));
export const UpdateSecurityDetailsShape = UpdateSecurityShape.and(
	z
		.object({
			nominalValue: z.preprocess(
				preprocessEmptyStringAsUndefined,
				z.number().nonnegative().optional()
			),
			votesPerUnit: z.preprocess(
				preprocessEmptyStringAsUndefined,
				z.number().nonnegative().optional()
			),
			residueRights: z.boolean().optional().nullable(),
			includeInReturns: z.boolean().optional(),
			issuedIn: z.nativeEnum(SecurityIssuedIn).optional(),
			equityBasis: z.nativeEnum(SecurityEquityBasis).optional().nullable(),
			allowFractionalOwnership: z.boolean().optional(),
			unitDenominator: z.number().positive().int().optional().nullable(),
			isMgtLoan: z.boolean().optional().nullable(),
			loanDefinitionId: z.string().uuid().optional().nullable(),
		})
		.refine(
			(data) => {
				if (data.isMgtLoan) {
					return !!data.loanDefinitionId;
				}
				return true;
			},
			{
				message: 'Loan definition is required for management loans',
				path: ['loanDefinitionId'],
			}
		)
);

export type UpdateSecurityFormDto = z.infer<typeof UpdateSecurityShape>;
export type UpdateSecurityDetailsFormDto = z.infer<typeof UpdateSecurityDetailsShape>;

export const UpdateSecurityCurrencyShape = (
	tCommonForm: TranslateFunction<I18Namespaces.COMMON_FORM>
) =>
	z
		.object({
			currentCurrency: z.nativeEnum(CurrencyType).optional(),
			newCurrency: z.nativeEnum(CurrencyType).nullable(),
		})
		.refine(
			(data) => {
				return !!data.newCurrency;
			},
			{
				message: tCommonForm('field_required'),
				path: ['newCurrency'],
			}
		);
export type UpdateSecurityCurrencyFormDto = z.infer<ReturnType<typeof UpdateSecurityCurrencyShape>>;
